/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * ValidationSummary + IssueDot (guidance §16) — the shared way every mapping
 * editor renders its {@link MappingIssue}s. Generalizes the banner the ORTB body
 * editor grew inline so the parameter, header, response-mapping, and pixel
 * editors read identically.
 *
 * The banner is fix-shape-agnostic: it renders a **Fix** for any `fixable` issue
 * and calls back with the issue + its index, and a **Fix all** when `onFixAll`
 * is provided. The editor owns the concrete fix payload and applies it — index
 * into the same array it passed in.
 */

import React from 'react';
import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { useFindingsListOwner } from './findings-list-owner';
import type { FindingReview } from './row-open-request';
import {
  type MappingIssue,
  type MappingIssueLevel,
  ISSUE_DOT,
  ISSUE_INK,
  ISSUE_TINT,
  countByLevel,
  issueLevel,
  worstLevel,
} from './mapping-issues';

export interface ValidationSummaryProps {
  issues: MappingIssue[];
  /**
   * Apply this issue's fix in place. Called only for `fixable` issues.
   *
   * **Required and nullable, never optional** (`#1134`, `#1254`). It was optional and three of the
   * five editors passed nothing — so every finding they raised appeared with **no action and no
   * explanation**, which is the silence `pattern 24` is about: *a disabled control with a reason is
   * a statement; an absent one is a silence.* `null` is a surface saying it cannot apply anything
   * here, and it is stated once at the head of the list rather than on every row (`#1248` — state a
   * claim once, at the level it is true at).
   */
  onFix: ((issue: MappingIssue, index: number) => void) | null;
  /**
   * **Take the operator to the field a finding names**, for a finding with nothing to apply — or
   * `null` where this list cannot point at a row. The same Review the page panel offers (`#1305`),
   * so a finding does not offer `Review` in one list and *no auto-fix* in the other.
   *
   * Required and nullable, like `onFix`.
   */
  review: FindingReview | null;
}

/** A small solid dot at the worst severity — for section headers and the step rail. */
export const IssueDot: React.FC<{ level: MappingIssueLevel | null; title?: string }> = ({ level, title }) => {
  if (!level) return null;
  return (
    <Box
      as="span"
      display="inline-block"
      w="8px"
      h="8px"
      borderRadius="full"
      bg={ISSUE_DOT[level]}
      flexShrink={0}
      title={title}
      // A role, or the name is dropped: `aria-label` on a bare span is not announced (`#1412`).
      role="img"
      aria-label={title ?? `${level} issues present`}
    />
  );
};

export const ValidationSummary: React.FC<ValidationSummaryProps> = ({ issues, onFix, review }) => {
  const owner = useFindingsListOwner();
  // The page lists these already, with a breadcrumb naming the editor and the field. Rendering them
  // here too is the same sentence twice on one screen, and the two copies disagreed about what could
  // be done about each one (`#1254`).
  if (owner === 'page') return null;
  if (issues.length === 0) return null;

  const counts = countByLevel(issues);
  const summaryParts = (['error', 'warn', 'info'] as const)
    .filter((lvl) => counts[lvl] > 0)
    .map((lvl) => `${counts[lvl]} ${lvl === 'warn' ? 'warning' : lvl}${counts[lvl] === 1 ? '' : 's'}`);

  return (
    <VStack align="stretch" gap={0} borderWidth="1px" borderColor="border.default" borderRadius="8px" overflow="hidden">
      <HStack px={3} py={2} gap={2} bg="bg.subtle" borderBottomWidth="1px" borderColor="border.default">
        <IssueDot level={worstLevel(issues)} />
        <Text fontSize="xs" fontFamily="mono" textTransform="uppercase" color="fg.muted" mr="auto">
          {summaryParts.join(' · ')}
        </Text>
        {/**
         * **`Fix all` moved out of this banner** (`#1254`). It is an editor capability — *add the
         * fields the checks name* — not a rendering of findings, so tying it to the list meant it
         * disappeared with the list on the surface where the page draws them. It now sits in each
         * editor's own add-a-field area, named for the act it performs, and it renders on **both**
         * surfaces rather than only where the banner survives.
         */}
      </HStack>
      {/**
       * **An editor with no automatic fix at all says so once** (`#1254`, `#1248`).
       *
       * Three of the five passed no handler, so every finding they raised appeared with no action
       * and no explanation. The claim is true of the whole list there, so it goes at the head of it —
       * repeating *no auto-fix* down twenty rows teaches nothing and buries the rows that differ.
       *
       * **It is about fixes, not about permission.** Those editors are perfectly editable; what they
       * lack is anything to apply automatically. *Nothing here can be changed* would have been a
       * false claim on a surface the operator is typing into — `#1045`'s *state what was done, not
       * what was implied*, caught while writing this sentence rather than after.
       */}
      {onFix === null && issues.length > 0 && (
        <Text px={3} py={1.5} fontSize="xs" color="fg.muted" bg="bg.subtle">
          None of these have an automatic fix — open the field each one names and set it.
        </Text>
      )}
      {issues.map((issue, i) => (
        <HStack
          key={`${issue.ref ?? ''}-${i}`}
          px={3}
          py={2}
          gap={2}
          bg={ISSUE_TINT[issueLevel(issue)]}
          color={ISSUE_INK[issueLevel(issue)]}
          fontSize="sm"
        >
          <Text fontFamily="mono" fontSize="xs" textTransform="uppercase" fontWeight="600" flexShrink={0}>
            {issueLevel(issue)}
          </Text>
          <Text flex="1">{issue.message}</Text>
          {issue.ref && (
            <Text fontFamily="mono" fontSize="xs" opacity={0.75} flexShrink={0}>
              {issue.ref}
            </Text>
          )}
          {/**
           * **Three answers, not two** (`X33a`'s shape, `#1254`): *apply it*, *there is nothing to
           * apply*, and *this view cannot apply anything* — the last stated once above rather than
           * on every row.
           *
           * The middle one used to render **nothing**, which is how the same finding came to offer
           * `Review` at the top of the page and no action at all inside the editor. A short inert
           * label rather than a disabled button with a hover: there is no control being refused
           * here, so there is nothing for a tooltip to hang on, and a reason reachable only by
           * hovering is one a keyboard reader cannot get to at all.
           */}
          {/**
           * **Review before *no auto-fix***: a finding with nothing to apply still names a field, and
           * going to it is the next act — the page panel's answer to the same finding (`#1305`). The
           * label stays only where this editor holds no such row.
           */}
          {onFix !== null && issue.fixable ? (
            <Button
              size="xs"
              variant="outline"
              borderColor="currentColor"
              color={ISSUE_INK[issueLevel(issue)]}
              flexShrink={0}
              _hover={{ bg: 'bg.card' }}
              onClick={() => onFix(issue, i)}
            >
              Fix
            </Button>
          ) : review !== null && issue.ref && issue.at && review.reaches(issue.ref) ? (
            /**
             * **A field inside an expression is opened, not revealed** (`#1334`): it is drawn only
             * while its row is open, so a Review that changed nothing could not reach it. Named
             * *Fix*, as the page panel names the same finding — two words for one act in two lists
             * is the disagreement pattern 35 was written for.
             */
            <Button
              size="xs"
              variant="outline"
              borderColor="currentColor"
              color={ISSUE_INK[issueLevel(issue)]}
              flexShrink={0}
              _hover={{ bg: 'bg.card' }}
              title="Open this field to choose its value"
              aria-label={`Fix ${issue.ref}`}
              onClick={() => review.openAt(issue.ref!, issue.at!)}
            >
              Fix
            </Button>
          ) : review !== null && issue.ref && review.reaches(issue.ref) ? (
            <Button
              size="xs"
              variant="outline"
              borderColor="currentColor"
              color={ISSUE_INK[issueLevel(issue)]}
              flexShrink={0}
              _hover={{ bg: 'bg.card' }}
              title="Go to this field"
              aria-label={`Review ${issue.ref}`}
              onClick={() => review.go(issue.ref!)}
            >
              Review
            </Button>
          ) : (
            onFix !== null && (
              <Text fontSize="xs" opacity={0.7} flexShrink={0} whiteSpace="nowrap">
                no auto-fix
              </Text>
            )
          )}
        </HStack>
      ))}
    </VStack>
  );
};
